import { useMemo } from 'react';
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { ChannelListParams, ChannelListResponse, Channel } from '@src/models/channel';
import { applicationApi } from '@src/apis/admin';
import type { PlanType as ApiPlanType, ApplicationListResponseModelPaginatedResponse, ApplicationListResponseModel } from '@uniquegood/realworld-admin-interface';
import type { PlanType } from '@src/pages/Studio/Channel/constants';

type ChannelListQueryKey = readonly ['channels', ChannelListParams];

export interface UseChannelListOptions {
    /**
     * React Query 옵션을 덮어씁니다.
     * `queryKey`와 `queryFn`은 내부에서 관리하므로 제외했습니다.
     */
    queryOptions?: Omit<
        UseQueryOptions<ChannelListResponse, Error, ChannelListResponse, ChannelListQueryKey>,
        'queryKey' | 'queryFn'
    >;
}

/**
 * 채널 목록 조회 훅
 *
 * @description
 * - 검색, 필터, 페이지네이션 파라미터를 받아 채널 목록을 조회합니다.
 * - ApplicationApi.getApplications를 사용하여 실제 API를 호출합니다.
 */
export const useChannelList = (
    params: ChannelListParams,
    options: UseChannelListOptions = {}
): UseQueryResult<ChannelListResponse> => {
    const { queryOptions } = options;
    const { staleTime, ...restQueryOptions } = queryOptions ?? {};

    const queryKey = useMemo<ChannelListQueryKey>(() => ['channels', params] as const, [params]);

    return useQuery<ChannelListResponse, Error, ChannelListResponse, ChannelListQueryKey>({
        queryKey,
        queryFn: async () => {
            // PlanFilter를 API PlanType으로 변환 (대문자로 시작)
            const planTypeMap: Record<PlanType, ApiPlanType> = {
                'free': 'Free' as ApiPlanType,
                'pro': 'Pro' as ApiPlanType,
                'enterprise': 'Enterprise' as ApiPlanType,
                'teacher': 'Teacher' as ApiPlanType,
                'class': 'Class' as ApiPlanType,
                'realworld': 'Realworld' as ApiPlanType,
            };
            const planType: ApiPlanType | undefined = params.planFilter === 'all' 
                ? undefined 
                : planTypeMap[params.planFilter as PlanType];
            
            const { data } = await applicationApi.getApplications(
                '1.1', // x-rwd-api-version
                params.searchTerm || undefined, // Keyword
                planType, // PlanType
                undefined, // HasPendingPaymentRequest
                params.page, // page
                params.limit, // take
            );
            
            // API 응답 타입: ApplicationListResponseModelPaginatedResponseApiSuccessResponseModel
            // data.data가 ApplicationListResponseModelPaginatedResponse
            const paginatedResponse: ApplicationListResponseModelPaginatedResponse = data.data;
            const applications:Array<ApplicationListResponseModel> = paginatedResponse?.data ?? [];
            
            const channels: Channel[] = applications.map((app, index: number) => {
                // API에서 받은 PlanType을 소문자로 변환 (예: 'Free' -> 'free')
                const planType = app.planType?.toLowerCase() as Channel['plan'] | undefined;
                
                // channelId가 없거나 빈 문자열인 경우 인덱스 기반 고유 ID 생성
                const channelId = app.applicationId || `temp-${index}`;
                
                return {
                    channelId,
                    channelName: app.applicationName ?? '',
                    channelIntroduction: undefined, // ApplicationListResponseModel에 없음
                    createdAt: app.createdAt,
                    contentCount: app.contentCount,
                    plan: planType,
                    status: 'active', // ApplicationListResponseModel에 status 필드 없음, 기본값 사용
                };
            });

            return {
                channels,
                total: paginatedResponse?.count ?? 0,
                page: paginatedResponse?.pageIndex ?? params.page,
                limit: params.limit,
            };
        },
        staleTime: staleTime ?? 30 * 1000,
        ...restQueryOptions,
    });
};

